import { useParams } from "react-router-dom";
import { EmptyState, ErrorPage, ErrorState, SkeletonStorefront } from "@branding/ui";
import { Button } from "@heroui/react";
import useSWR from "swr";
import { JsonLd } from "../components/JsonLd";
import { ProductCard } from "../components/ProductCard";
import { fetchStore } from "../lib/api";
import { publicOrigin } from "../lib/format";
import { storeJsonLd } from "../lib/json-ld";
import { useSsrData } from "../lib/ssr-data";
import type { Store } from "../lib/types";
import { ApiClientError } from "../lib/v2";

export function StorePage() {
  const { slug = "" } = useParams();
  const ssr = useSsrData();
  const fallback =
    ssr?.kind === "store" && ssr.store?.slug === slug ? ssr.store : undefined;
  const { data, error, isLoading, mutate } = useSWR<Store>(
    slug ? `toko:${slug}` : null,
    () => fetchStore(slug),
    { fallbackData: fallback },
  );

  if (error instanceof ApiClientError && error.status === 404) {
    return <ErrorPage code={404} actionHref="/pasar" />;
  }
  if (error) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <ErrorState
          title="Toko tidak bisa dibuka"
          onRetry={() => {
            void mutate();
          }}
        />
      </main>
    );
  }
  if (isLoading && !data) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <SkeletonStorefront />
      </main>
    );
  }
  if (!data) return <ErrorPage code={404} actionHref="/pasar" />;

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
      <JsonLd data={storeJsonLd(data, publicOrigin())} />
      <p className="text-sm">
        <a href="/pasar">Pasar Kupang</a>
      </p>
      <h1 className="mt-3 text-3xl font-semibold">{data.name}</h1>
      {data.description ? (
        <p className="mt-3 max-w-prose text-muted">{data.description}</p>
      ) : null}
      <div className="mt-8">
        {data.products.length === 0 ? (
          <EmptyState
            title="Belum ada produk"
            description="Toko ini belum menampilkan etalase publik."
            action={
              <Button variant="primary" onPress={() => window.location.assign("/pasar")}>
                Jelajahi pasar
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
